import axios from "axios";
import * as Sentry from "@sentry/react";

// Type definitions for the function parameters
interface AttributionData {
  currentUrl: string;
  referrer?: string;
  fbclid?: string | null;
  utm_campaign?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
  grade?: string | null;
  username?: string | null;
  phone_number?: string | null;
  eventName: string | null;
  additionalData?: {
    success?: boolean;
    payment_type?: string;
    sale?: {
      product_type?: string;
      amount_inr?: number;
      order_id?: string;
    };
    // Extended fields for coupon and other event data
    coupon_code?: string;
    discount_amount?: number;
    original_amount?: number;
    final_amount?: number;
    source?: string;
    [key: string]: any; // Allow any additional properties
  } | null;
}

// Helper function to get a cookie by name
export const getCookie = (name: string): string | null => {
  const cookieArr = document.cookie.split(";");
  for (const cookie of cookieArr) {
    const [key, value] = cookie.trim().split("=");
    if (key === name) {
      return value;
    }
  }
  return null; // Return null if the cookie does not exist
};

// Helper function to set a cookie
export const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; domain=nationalfinanceolympiad.com; secure; SameSite=None`;
};

// Helper function to validate grade UTM parameter
export const validateGradeUtm = (grade: string | null): string | null => {
  if (!grade) return null;

  const gradeNum = parseInt(grade, 10);
  if (isNaN(gradeNum) || gradeNum < 5 || gradeNum > 12) {
    console.warn(
      `Invalid grade UTM parameter: ${grade}. Must be between 5-12.`
    );
    return null;
  }

  return grade;
};

// Helper function to conditionally include properties in an object
const conditionalInclude = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const result: Partial<T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined && value !== '') {
      result[key as keyof T] = value;
    }
  }
  return result;
};

export const sendAttributionData = async ({
  currentUrl = "",
  referrer = document.referrer || "",
  fbclid = null,
  utm_campaign = null,
  utm_source = null,
  utm_medium = null,
  utm_term = null,
  utm_content = null,
  grade = null,
  username = null,
  phone_number = null,
  eventName = null,
  additionalData = null,
}: AttributionData): Promise<any> => {
  if (!currentUrl) {
    console.error("currentUrl is required");
    return;
  }

  // Retrieve session_id from cookies
  const session_id = getCookie("session_id") || null;
  const fbc = getCookie("_fbc") || null;
  const fbp = getCookie("_fbp") || null;
  const ga = getCookie("_ga") || null;
  const ga_measurement= getCookie("_ga_ZGFSQKYL39") || null;

  const filled_username = sessionStorage.getItem("name") || null;
  const filled_phone_number = sessionStorage.getItem("mobileNumber") || null;

  const exp_name = sessionStorage.getItem("exp_name") || null;
  const exp_variant = sessionStorage.getItem("exp_variant") || null;
  const exp_id = sessionStorage.getItem("exp_id") || null;

  // Fetch coupon data from sessionStorage if available
  const appliedCouponCode = sessionStorage.getItem("appliedCouponCode") || null;
  const couponDiscountAmount = sessionStorage.getItem("couponDiscountAmount") || null;
  const originalAmount = sessionStorage.getItem("originalAmount") || null;

  try {
    const url = new URL(currentUrl);

    const data = {
      currentUrl: url.hostname,
      currentPath: url.pathname,
      referrer,
      source: "nfo-checkout",
      event_name: eventName,
      additional_data: {
        event_name: eventName,
        ...additionalData,
        // Include coupon data from sessionStorage if available
        ...conditionalInclude({
          coupon_code: appliedCouponCode,
          discount_amount: couponDiscountAmount ? parseFloat(couponDiscountAmount) : null,
          original_amount: originalAmount ? parseFloat(originalAmount) : null,
          final_amount: appliedCouponCode && couponDiscountAmount && originalAmount 
            ? parseFloat(originalAmount) - parseFloat(couponDiscountAmount) 
            : null,
        }),
      },
      // Conditionally include tracking parameters only if they exist
      ...conditionalInclude({
        fbclid,
        fbc,
        fbp,
        ga,
        ga_measurement,
        exp_name,
        exp_variant,
        exp_id,
        utm_campaign,
        utm_source,
        utm_medium,
        utm_term,
        utm_content,
        grade,
      }),
    };

    const payload = {
      data,
      // Conditionally include user data only if they exist
      ...conditionalInclude({
        username: filled_username,
        session_id,
        phone_number: filled_phone_number,
      }),
    };

    const response = await axios.post(
      "https://admin.arb.wiki/channelAttribution/store-attribution",
      payload,
      {
        headers: {
          "x-api-key": "lZxTPXggnP4ygq4PKaVjCQWdRVCYFrz4uxwWf3H3",
          "Content-Type": "application/json",
        },
      }
    );

    console.log("API Response:", response.data);

    if (response.data.session_id && !session_id) {
      setCookie("session_id", response.data.session_id, 30);
      console.log("New session_id saved in cookies:", response.data.session_id);
    }

    return response.data;
  } catch (err: any) {
    console.error("Error sending attribution data:", err);
    throw err;
  }
};

export const trackData = async (
  eventName: string | null,
  userName?: string | null,
  phoneNumber?: string | null,
  additionalData?: {
    success?: boolean;
    payment_type?: string;
    sale?: {
      product_type?: string;
      amount_inr?: number;
      order_id?: string;
    };
    // Extended fields for coupon and other event data
    coupon_code?: string;
    discount_amount?: number;
    original_amount?: number;
    final_amount?: number;
    source?: string;
    [key: string]: any; // Allow any additional properties
  }
) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);

    // For each UTM param: prefer URL (most specific), fall back to sessionStorage
    // (saved on first landing when the ad URL had the params)
    const resolveParam = (key: string) =>
      urlParams.get(key) || sessionStorage.getItem(key) || null;

    const response = await sendAttributionData({
      currentUrl: window.location.href,
      referrer: document.referrer,
      eventName: eventName,
      additionalData: additionalData,
      // Conditionally include URL parameters only if they exist
      ...conditionalInclude({
        fbclid: resolveParam("fbclid"),
        utm_campaign: resolveParam("utm_campaign"),
        utm_term: resolveParam("utm_term"),
        utm_content: resolveParam("utm_content"),
        utm_source: resolveParam("utm_source"),
        utm_medium: resolveParam("utm_medium"),
        grade: validateGradeUtm(urlParams.get("grade")),
        username: userName,
        phone_number: phoneNumber,
      }),
    });
    console.log("Tracking response:", response);
  } catch (err) {
    console.error("Error tracking data:", err);
    Sentry.captureException(err);
  }
};
